import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useDebounce } from "@/hooks";

export interface AutocompleteOption {
  label: string;
  value: string | number;
}

interface DebouncedAutocompleteInputProps {
  /** Texto exibido no placeholder */
  placeholder: string;
  /** Tooltip descritivo */
  tooltip?: string;
  /** Valor atual do input (texto digitado) */
  inputValue: string;
  /** Callback chamado quando o texto muda */
  onInputChange: (value: string) => void;
  /** Callback chamado quando um item é selecionado — recebe label e id */
  onSelect: (label: string, id: string | number) => void;
  /** Callback chamado quando o campo é limpo */
  onClear: () => void;
  /** Função assíncrona que busca sugestões dado um termo */
  fetchOptions: (q: string) => Promise<AutocompleteOption[]>;
  /** Mínimo de caracteres para disparar a busca (padrão: 3) */
  minChars?: number;
  /** Delay do debounce em ms (padrão: 400) */
  debounceDelay?: number;
  /** Se true, o campo está desabilitado */
  isDisabled?: boolean;
}

const DebouncedAutocompleteInput: React.FC<DebouncedAutocompleteInputProps> = ({
  placeholder,
  tooltip,
  inputValue,
  onInputChange,
  onSelect,
  onClear,
  fetchOptions,
  minChars = 3,
  debounceDelay = 400,
  isDisabled = false,
}) => {
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const debouncedQuery = useDebounce(inputValue, debounceDelay);

  // Busca sugestões quando o debounced value muda
  useEffect(() => {
    // Se o usuário já selecionou um item, não buscar novamente
    if (hasSelected) return;

    if (!debouncedQuery || debouncedQuery.trim().length < minChars) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    // Cancelar request anterior
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setIsLoading(true);
    setIsOpen(true);

    fetchOptions(debouncedQuery.trim())
      .then((results) => {
        setOptions(results);
        setActiveIndex(-1);
      })
      .catch((err) => {
        // Ignorar erros de abort
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error("[DebouncedAutocompleteInput] fetch error:", err);
        }
        setOptions([]);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      abortRef.current?.abort();
    };
  }, [debouncedQuery, minChars, hasSelected]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasSelected(false);
    onInputChange(e.target.value);
    if (e.target.value.trim().length < minChars) {
      setOptions([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (option: AutocompleteOption) => {
    setHasSelected(true);
    setIsOpen(false);
    setOptions([]);
    onSelect(option.label, option.value);
  };

  const handleClear = () => {
    setHasSelected(false);
    setOptions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    onClear();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || options.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(options[activeIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const inputEl = (
    <InputGroup>
      <Input
        placeholder={placeholder}
        bg="white"
        height="3rem"
        color="black"
        fontFamily="Lexend Deca"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (!hasSelected && options.length > 0) setIsOpen(true);
        }}
        isDisabled={isDisabled}
        autoComplete="off"
        pr={inputValue ? "2.5rem" : undefined}
      />
      {inputValue && (
        <InputRightElement height="3rem">
          {isLoading ? (
            <Spinner size="xs" color="gray.400" />
          ) : (
            <CloseIcon
              boxSize={3}
              color="gray.400"
              cursor="pointer"
              onClick={handleClear}
              aria-label="Limpar filtro"
            />
          )}
        </InputRightElement>
      )}
    </InputGroup>
  );

  return (
    <Box ref={containerRef} position="relative" flex={1} minW="160px">
      {tooltip ? (
        <Tooltip label={tooltip} fontSize="md">
          {inputEl}
        </Tooltip>
      ) : (
        inputEl
      )}

      {isOpen && (
        <Box
          position="absolute"
          top="calc(3rem + 4px)"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="220px"
          overflowY="auto"
        >
          {isLoading ? (
            <Box p={3} textAlign="center">
              <Spinner size="sm" color="teal.500" />
            </Box>
          ) : options.length === 0 ? (
            <Box p={3}>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Nenhum resultado encontrado
              </Text>
            </Box>
          ) : (
            <List>
              {options.map((opt, idx) => (
                <ListItem
                  key={`${opt.value}-${idx}`}
                  px={4}
                  py={2}
                  cursor="pointer"
                  fontSize="sm"
                  fontFamily="Lexend Deca"
                  color="gray.800"
                  bg={idx === activeIndex ? "teal.50" : "white"}
                  _hover={{ bg: "teal.50" }}
                  onMouseDown={(e) => {
                    // mousedown antes do blur para garantir seleção
                    e.preventDefault();
                    handleSelect(opt);
                  }}
                  aria-selected={idx === activeIndex}
                >
                  {opt.label}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DebouncedAutocompleteInput;
